import { getMenu } from "@koala/sdk/v4";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { DELIVERY_TIME_WANTED_MODES } from "@/constants/global";
import { createHttpClient } from "@/services/client";
import { __DANGEROUS_EMPTY_REQUEST_ORIGIN, getOrigin } from "@/utils";
import { objectifyOptions } from "@/utils/events";

interface MenuService {
  id: number | undefined;
  ifModifiedSince?: string;
  wantedAt?: string;
}

/**
 * A store location id is only usable once it has hydrated to a real, positive
 * numeric id. The client transiently holds `undefined`/`null`/`0` before the
 * location resolves; firing the menu request with any of those hits
 * `store-locations/0/menu` and returns a guaranteed 404
 * (`Unable to find StoreLocation`). (TEAM-13663)
 */
export function isResolvedLocationId(id: number | undefined): id is number {
  return typeof id === "number" && id > 0;
}

function resolveRequestOrigin(): string {
  return typeof window !== "undefined"
    ? getOrigin(window.location.host)
    : __DANGEROUS_EMPTY_REQUEST_ORIGIN;
}

export function getMenuQueryKey({
  id,
  ifModifiedSince,
  wantedAt,
}: MenuService) {
  return [
    "menu",
    {
      id,
      ifModifiedSince,
      wantedAt: wantedAt ?? DELIVERY_TIME_WANTED_MODES.ASAP,
    },
  ];
}

export function useMenu({ id, ifModifiedSince, wantedAt }: MenuService) {
  const client = createHttpClient({ origin: resolveRequestOrigin() });
  const { data, status, fetchStatus } = useQuery({
    queryKey: getMenuQueryKey({ id, ifModifiedSince, wantedAt }),
    queryFn: () =>
      getMenu(
        {
          id: id ?? 0,
          ifModifiedSince,
          wantedAt:
            wantedAt === DELIVERY_TIME_WANTED_MODES.ASAP ? undefined : wantedAt,
          compressed: true,
        },
        { client }
      ),
    // Only fire once the location id has hydrated to a real, positive id. A
    // `0`/`undefined` id produces an `Unable to find StoreLocation` 404 followed
    // by a re-fetch. (TEAM-13663)
    enabled: isResolvedLocationId(id),
    refetchOnWindowFocus: false,
    staleTime: wantedAt === DELIVERY_TIME_WANTED_MODES.ASAP ? 120000 : 300000,
    keepPreviousData: true,
  });

  useEffect(() => {
    const categories = data?.[0];
    if (!categories) {
      return;
    }

    // Defer the recursive analytics option-mapping off the critical render
    // path so it does not block first menu paint on large menus. It only feeds
    // interaction-time analytics, so running it at idle is safe. (TEAM-13663)
    if (
      typeof window !== "undefined" &&
      typeof window.requestIdleCallback === "function"
    ) {
      const handle = window.requestIdleCallback(
        () => objectifyOptions(categories),
        { timeout: 2000 }
      );

      return () => window.cancelIdleCallback(handle);
    }

    const handle = setTimeout(() => objectifyOptions(categories), 0);

    return () => clearTimeout(handle);
  }, [data]);

  return {
    categories: data?.[0] ? data[0] : [],
    status,
    isLoading: fetchStatus === "fetching",
  };
}

/**
 * Warm the menu cache for a location ahead of navigation (e.g. on location card
 * hover/select) so the menu request overlaps the route transition instead of
 * starting only after the store page mounts. Uses the same query key as
 * {@link useMenu} so the store page consumes the warmed entry rather than
 * issuing its own fetch. (TEAM-13663)
 */
export function usePrefetchMenu() {
  const queryClient = useQueryClient();

  return (id: number | undefined) => {
    // Mirror the useMenu gate: never warm the cache with an unresolved id.
    if (!isResolvedLocationId(id)) {
      return;
    }

    const client = createHttpClient({ origin: resolveRequestOrigin() });

    void queryClient.prefetchQuery(
      getMenuQueryKey({ id }),
      () =>
        getMenu(
          { id, ifModifiedSince: undefined, compressed: true },
          { client }
        ),
      { staleTime: 120000 }
    );
  };
}
